import type { SaveData } from '../types';

const SAVE_KEY = 'pathforge_save';

export class SaveManager {
  private data: SaveData;

  constructor() {
    this.data = this.load() ?? this.createDefaultSave();
  }

  public getData(): SaveData {
    return this.data;
  }

  public saveCurrent(): void {
    this.save(this.data);
  }

  public save(data?: SaveData): void {
    if (data) this.data = data;
    try {
      localStorage.setItem(SAVE_KEY, JSON.stringify(this.data));
    } catch (e) {
      console.error('Failed to save game', e);
    }
  }

  public load(): SaveData | null {
    try {
      const raw = localStorage.getItem(SAVE_KEY);
      if (!raw) return null;
      return this.migrate(JSON.parse(raw));
    } catch (e) {
      console.error('Failed to load game', e);
      return null;
    }
  }

  public reset(): void {
    this.data = this.createDefaultSave();
    this.save();
  }

  public export(): string {
    return btoa(JSON.stringify(this.data));
  }

  public import(code: string): boolean {
    try {
      const data = JSON.parse(atob(code));
      if (this.validate(data)) {
        this.data = this.migrate(data);
        this.save();
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }

  public completeLevel(levelId: string, stars: number): void {
    if (!this.data.progress.campaign[levelId]) {
      this.data.progress.campaign[levelId] = {};
    }
    const current = this.data.progress.campaign[levelId]['normal'] ?? 0;
    if (stars > current) {
      this.data.progress.campaign[levelId]['normal'] = stars;
      this.data.player.xp += (stars - current) * 100;
      this.data.player.level = 1 + Math.floor(this.data.player.xp / 300);
    }
    this.save();
  }

  public updateStats(stats: { totalKills?: number; totalBossKills?: number; highestWave?: number }): void {
    if (stats.totalKills !== undefined) {
      this.data.stats.totalKills += stats.totalKills;
    }
    if (stats.totalBossKills !== undefined) {
      this.data.stats.totalBossKills += stats.totalBossKills;
    }
    if (stats.highestWave !== undefined && stats.highestWave > this.data.stats.highestWave) {
      this.data.stats.highestWave = stats.highestWave;
    }
    this.save();
  }

  public unlockAchievement(id: string): void {
    if (!this.data.progress.achievements.includes(id)) {
      this.data.progress.achievements.push(id);
      this.save();
    }
  }

  public unlockTower(id: string): void {
    if (!this.data.progress.unlocks.includes(id)) {
      this.data.progress.unlocks.push(id);
      this.save();
    }
  }

  public unlockTowers(ids: string[]): void {
    let changed = false;
    for (const id of ids) {
      if (!this.data.progress.unlocks.includes(id)) {
        this.data.progress.unlocks.push(id);
        changed = true;
      }
    }
    if (changed) this.save();
  }

  private createDefaultSave(): SaveData {
    return {
      version: 1,
      player: { level: 1, xp: 0 },
      progress: {
        campaign: {},
        unlocks: [],
        achievements: [],
      },
      settings: {
        masterVolume: 1,
        musicVolume: 0.4,
        sfxVolume: 0.7,
        quality: 'high',
        showPathPreview: true,
        particleEffects: true,
      },
      stats: {
        totalKills: 0,
        totalBossKills: 0,
        highestWave: 0,
      },
    };
  }

  private migrate(raw: any): SaveData {
    const defaults = this.createDefaultSave();
    const data = raw && typeof raw === 'object' ? raw : {};
    const player = data.player && typeof data.player === 'object' ? data.player : {};
    const progress = data.progress && typeof data.progress === 'object' ? data.progress : {};
    const settings = data.settings && typeof data.settings === 'object' ? data.settings : {};
    const stats = data.stats && typeof data.stats === 'object' ? data.stats : {};
    const quality = ['low', 'medium', 'high'].includes(settings.quality) ? settings.quality : defaults.settings.quality;

    return {
      version: 1,
      player: {
        level: Math.max(1, this.toNonNegativeInteger(player.level, defaults.player.level)),
        xp: this.toNonNegativeInteger(player.xp, defaults.player.xp),
      },
      progress: {
        campaign: this.toCampaign(progress.campaign),
        unlocks: this.toStringArray(progress.unlocks),
        achievements: this.toStringArray(progress.achievements),
      },
      settings: {
        masterVolume: this.toVolume(settings.masterVolume, defaults.settings.masterVolume),
        musicVolume: this.toVolume(settings.musicVolume, defaults.settings.musicVolume),
        sfxVolume: this.toVolume(settings.sfxVolume, defaults.settings.sfxVolume),
        quality,
        showPathPreview: typeof settings.showPathPreview === 'boolean' ? settings.showPathPreview : true,
        particleEffects: typeof settings.particleEffects === 'boolean' ? settings.particleEffects : true,
      },
      stats: {
        totalKills: this.toNonNegativeInteger(stats.totalKills, 0),
        totalBossKills: this.toNonNegativeInteger(stats.totalBossKills, 0),
        highestWave: this.toNonNegativeInteger(stats.highestWave, 0),
      },
    };
  }

  private validate(data: any): boolean {
    return !!data && typeof data === 'object' && typeof data.version === 'number';
  }

  private toNonNegativeInteger(value: unknown, fallback: number): number {
    return typeof value === 'number' && Number.isFinite(value)
      ? Math.max(0, Math.floor(value))
      : fallback;
  }

  private toVolume(value: unknown, fallback: number): number {
    return typeof value === 'number' && Number.isFinite(value)
      ? Math.max(0, Math.min(1, value))
      : fallback;
  }

  private toStringArray(value: unknown): string[] {
    if (!Array.isArray(value)) return [];
    return [...new Set(value.filter((item): item is string => typeof item === 'string'))];
  }

  private toCampaign(value: unknown): SaveData['progress']['campaign'] {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
    const campaign: SaveData['progress']['campaign'] = {};
    for (const [levelId, rawProgress] of Object.entries(value)) {
      if (!rawProgress || typeof rawProgress !== 'object' || Array.isArray(rawProgress)) continue;
      const levelProgress: Record<string, number> = {};
      for (const [mode, stars] of Object.entries(rawProgress)) {
        if (typeof stars === 'number' && Number.isFinite(stars)) {
          levelProgress[mode] = Math.max(0, Math.min(3, Math.floor(stars)));
        }
      }
      campaign[levelId] = levelProgress;
    }
    return campaign;
  }
}

export const saveManager = new SaveManager();
